import React from 'react';
import { builder } from '@builder.io/react';

import Image from 'next/legacy/image';
import { useRouter } from 'next/router';
import ErrorPage from 'next/error';

import Layout from 'react-scale/components/pages/blog/layout';
import Spinner from 'react-scale/components/elements/spinner';

import Header from 'react-scale/components/elements/header';
import Hero from 'react-scale/components/pages/resources/detail/hero';
import TryBanner from 'react-scale/components/sections/cta/simple-centered';
import Footer from 'react-scale/components/elements/footer';

export default function DownloadPage({ resource, preview }) {
  const router = useRouter();

  if (!router.isFallback && !resource?.slug) {
    return <ErrorPage statusCode={404} />;
  }

  return (
    <Layout preview={preview}>
      <>
        {router.isFallback ? (
          <div className="flex items-center justify-center h-screen">
            <Spinner size="lg" />
          </div>
        ) : (
          <div className="text-white bg-black">
            <div className="select-none absolute w-full h-[776px]">
              <div className="relative w-full h-full">
                <Image
                  src="/static/images/pages/transformx/speakers-top-colors.svg"
                  layout="fill"
                  objectFit="cover"
                />
              </div>
            </div>
            <Header
              canonical={`https://scale.com/resources/download/${resource.slug}`}
              borderColor="border-white/10"
              border
            />
            <div className="container relative space-y-20 lg:space-y-32">
              <Hero
                title={resource.title}
                subtitle={resource.subtitle}
                description={resource.description}
                image={{
                  url: resource.image,
                  alt: `${resource.title} - Hero image`,
                  width: resource.imageWidth ?? 1024,
                  height: resource.imageHeight ?? 500
                }}
                portalId={resource.portalId}
                formId={resource.formId}
                sfdcCampaignId={resource.sfdcCampaignId}
                modalTitle={resource.modalTitle}
                buttonText={resource.downloadButtonText}
                downloadLink={resource.downloadLink}
                withOpenDataset={resource.openDatasets}
                downloadSubtitle={resource.downloadSubtitle}
                downloadValidation={resource.downloadValidation}
                directDownload={resource.directDownload}
                modalButtonText={resource.modalButtonText}
                slug={resource.slug}
              />
            </div>
            <div className="mt-28">
              <TryBanner border={!resource.bottomSectionText || !resource.openDatasets} />
            </div>
            <Footer
              textColor="text-neutral-400"
              textHoverColor="text-white"
              borderColor="border-neutral-800"
              border
            />
          </div>
        )}
      </>
    </Layout>
  );
}

export async function getStaticPaths() {
  const allPages = await builder.getAll('resources-download-model', {
    options: { noTargeting: true },
    omit: 'data.blocks'
  });

  const pages = allPages.filter(page => page.data.slug);

  const paths = pages.map(page => ({ params: { slug: `${page.data.slug}` } }));

  return {
    paths,
    fallback: true
  };
}

export async function getStaticProps({ params }) {
  const builderResource = await builder
    .get('resources-download-model', {
      query: { 'data.slug': params.slug }
    })
    .promise();

  return {
    props: {
      resource: builderResource.data,
      revalidate: 1
    }
  };
}
